import { useMemo, useState } from 'react';
import { BooleanInput, Form, Loading, RecordContextProvider, useDataProvider, useGetList, useRecordContext, useUpdate, useTranslate } from 'react-admin';
import { ListItemText, ListItem, ListItemButton, ListItemIcon, Stack, Typography, IconButton, Box, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { useRefreshNotificationCount } from '@/composables/useRefreshNotificationCount.js';
import { useNotificationCount } from '@/composables/useNotificationCount.js';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import PropTypes from 'prop-types';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import ShowIcon from '@/assets/icons/show-icon.svg?react';
import ServiceNewIcon from '@/assets/icons/service-new-icon.svg?react';
import ServiceFailedIcon from '@/assets/icons/service-failed-icon.svg?react';
import ServiceProgressIcon from '@/assets/icons/service-progress-icon.svg?react';
import NewDeviceNewIcon from '@/assets/icons/new-device-icon.svg?react';
import NewDeviceProductionDoneIcon from '@/assets/icons/production-done-device-icon.svg?react';
import NewDeviceReadyForDeploymentIcon from '@/assets/icons/ready-for-deployment-device-icon.svg?react';
import NotificationNewIcon from '@/assets/icons/notification-new-icon.svg?react';
import NotificationReadIcon from '@/assets/icons/notification-read-icon.svg?react';
import NotificationService2Icon from '@/assets/icons/notification-service2-icon.svg?react';
import NotificationProductsIcon from '@/assets/icons/notification-products-icon.svg?react';
import NotificationDefaultIcon from '@/assets/icons/notification-default-icon.svg?react';
import NotificationDevicesIcon from '@/assets/icons/notification-devices-icon.svg?react';
import NotificationLogisticsIcon from '@/assets/icons/notification-logistics-icon.svg?react';
import NotificationAnalyticalIcon from '@/assets/icons/notification-analytical-icon.svg?react';
import NotificationUsersIcon from '@/assets/icons/notification-users-icon.svg?react';
import ArrowRightSmallIcon from '@/assets/icons/arrow-right-small-icon.svg';
import { Palette } from '@/themes/themeOverrides/Palette';
import { styled } from '@mui/material/styles';

const StyledNotificationListView = styled(Box)(({ theme }) => ({
    '&.notification-list': {
        maxWidth: '492px',
        borderRadius: '0px !important',
        boxShadow: 'none !important',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            boxShadow: 'none !important',
        },
        '& .RaLoading-message': {
            padding: '30px 0 10px',
            '& h5, & p': {
                display: 'none',
            },
        },
        '& .notification-list-empty-text': {
            padding: '30px 0 10px',
            fontSize: '14px',
        },
        '& > .MuiStack-root': {
            marginTop: '16px',
            '& .MuiListItem-root': {
                padding: '0px !important',
                margin: '0 0 4px 0',
                overflow: 'hidden',
                borderRadius: '12px',
                '& a.MuiButtonBase-root': {
                    padding: '9px 16px',
                    backgroundColor: Palette.gray.gray50,
                    color: Palette.black,
                    textDecoration: 'none !important',
                    '&:hover': {
                        color: Palette.black,
                        [theme.breakpoints.up('md')]: {
                            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                        },
                    },
                    '& > span': {
                        marginLeft: '10px',
                        [theme.breakpoints.down('sm')]: {
                            marginLeft: '2px',
                        },
                        '& > .MuiIconButton-root': {
                            marginLeft: 0,
                            marginRight: '-4px',
                        },
                    },
                },
            },
            '& .MuiListItemText-secondary': {
                fontSize: '11px',
                color: Palette.gray.gray500,
                paddingTop: '11px',
            },
        },
        '& .notification-list-item-icon': {
            minWidth: '20px',
            width: '20px',
            marginRight: '17px',
            marginLeft: '3px',
        },
        '& .notification-list-item-content': {
            fontSize: '14px',
            lineHeight: '22px',
            letterSpacing: '-0.14px',
            fontWeight: '400',
            [theme.breakpoints.down('sm')]: {
                lineHeight: '22px',
            },
            '& .tickets': {
                margin: '9px 0 -3px 0',
                '&.starts-with-span': {
                    '& .ticket:first-of-type': {
                        color: Palette.gray.gray500,
                    },
                },
                '&.starts-with-text': {
                    margin: '1px 0 -3px 0',
                },
                '& .ticket': {
                    display: 'inline-block',
                    padding: '4px 6px',
                    backgroundColor: Palette.white,
                    borderRadius: '6px',
                    marginBottom: '2px',
                    fontWeight: '400',
                    fontSize: '11px',
                    lineHeight: '16px',
                    verticalAlign: 'middle',
                    '& svg': {
                        width: '16px',
                        height: '16px',
                        marginRight: '4px',
                        display: 'inline-block',
                        verticalAlign: 'middle',
                    },
                },
                '& i.arrow': {
                    width: '16px',
                    height: '16px',
                    margin: '0 4px',
                    backgroundImage: `url("${ArrowRightSmallIcon}")`,
                    backgroundSize: 'auto',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                },
            },
        },
    },
}));

const NotificationList = () => {
    const translate = useTranslate();
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const last30DaysFilter = useMemo(
        () => ({
            createdAt_gte: new Date(new Date().setDate(new Date().getDate() - 30)),
        }),
        [],
    );
    const filters = useMemo(
        () => ({
            ...last30DaysFilter,
            ...(showUnreadOnly ? { isRead: false } : {}),
        }),
        [last30DaysFilter, showUnreadOnly],
    );
    const {
        data = [],
        isLoading,
        refetch,
    } = useGetList('notification', {
        sort: { field: 'createdAt', order: 'DESC' },
        filter: filters,
        pagination: { page: 1, perPage: 100 },
    });

    return (
        <StyledNotificationListView className='notification-list'>
            <NotificationListView
                data={data}
                isLoading={isLoading}
                refetch={refetch}
                showUnreadOnly={showUnreadOnly}
                setShowUnreadOnly={setShowUnreadOnly}
                emptyText={<Typography className='notification-list-empty-text'>{translate('notification.noNewNotifications')}</Typography>}
            />
        </StyledNotificationListView>
    );
};

const StyledNotificationFilter = styled(Box)(({ theme }) => ({
    '&.notification-list-filter': {
        boxShadow: 'none !important',
        overflow: 'visible !important',
        borderRadius: '0px !important',
        '& form': {
            display: 'flex',
            gap: '12px',
            justifyContent: 'space-between',
            [theme.breakpoints.down('sm')]: {
                display: 'block',
            },
            '& .MuiFormGroup-root': {
                margin: 0,
                '& .MuiFormControlLabel-root': {
                    marginLeft: '0',
                },
                '& .MuiFormHelperText-root': {
                    display: 'none',
                },
                '& .MuiTypography-root': {
                    fontSize: '11px',
                    lineHeight: '16px',
                    fontWeight: '400',
                    paddingLeft: '8px',
                },
            },
            '& .MuiButtonBase-root.MuiButton-containedPrimary': {
                fontSize: '11px',
                lineHeight: '16px !important',
                fontWeight: '400',
                padding: '0',
                backgroundColor: 'transparent',
                boxShadow: 'none !important',
                [theme.breakpoints.down('sm')]: {
                    padding: '10px 0',
                    marginTop: '6px',
                },
                '&:hover': {
                    backgroundColor: 'transparent',
                },
                '&:disabled': {
                    color: Palette.gray.gray400,
                    backgroundColor: 'transparent',
                },
            },
        },
    },
}));

const NotificationListView = ({ data, isLoading, refetch, showUnreadOnly, setShowUnreadOnly, emptyText }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const refreshCount = useRefreshNotificationCount();
    const notificationCount = useNotificationCount();

    if (isLoading) {
        return <Loading />;
    }

    const handleReadFilter = event => {
        setShowUnreadOnly(event.target.checked);
    };

    const handleMarkAllAsRead = () => {
        dataProvider.post('notification/mark-all-as-read').then(() => {
            refetch();
            refreshCount();
        });
    };

    return (
        <>
            <StyledNotificationFilter className='notification-list-filter'>
                <Form>
                    <BooleanInput
                        source='showUnread'
                        label={translate('notification.showUnreadOnly')}
                        size='x-small'
                        onChange={handleReadFilter}
                        defaultValue={showUnreadOnly}
                    />
                    <CustomButton
                        label={translate('notification.markAllAsRead')}
                        size='small'
                        disabled={notificationCount === 0}
                        onClick={handleMarkAllAsRead}
                    />
                </Form>
            </StyledNotificationFilter>
            {data.length === 0 ? (
                emptyText
            ) : (
                <Stack>
                    {data.map(record => (
                        <RecordContextProvider key={record.id} value={record}>
                            <NotificationListItem />
                        </RecordContextProvider>
                    ))}
                </Stack>
            )}
        </>
    );
};

NotificationListView.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        }),
    ).isRequired,
    isLoading: PropTypes.bool.isRequired,
    refetch: PropTypes.func.isRequired,
    showUnreadOnly: PropTypes.bool.isRequired,
    setShowUnreadOnly: PropTypes.func.isRequired,
    emptyText: PropTypes.node.isRequired,
};

const ReadIconButton = () => {
    const record = useRecordContext();
    const refreshCount = useRefreshNotificationCount();
    const [update] = useUpdate(
        'notification',
        {
            id: record.id,
            data: { isRead: !record.isRead },
        },
        {
            onSuccess: refreshCount,
        },
    );

    return (
        <IconButton
            sx={{ marginLeft: 10, backgroundColor: 'transparent' }}
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                update();
            }}
        >
            {record.isRead ? <NotificationReadIcon /> : <NotificationNewIcon />}
        </IconButton>
    );
};

const NotificationContent = ({ content }) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    const renderIcon = iconClass => {
        switch (iconClass) {
            case 'service-request-new':
                return <ServiceNewIcon />;
            case 'service-request-failed-contact':
                return <ServiceFailedIcon />;
            case 'service-request-accepted':
                return <ServiceProgressIcon />;
            case 'service-request-pending':
                return <ServiceProgressIcon />;
            case 'service-request-in-progress':
                return <ServiceProgressIcon />;
            case 'service-request-fixed':
                return <ServiceProgressIcon />;
            case 'service-request-closed':
                return <ServiceProgressIcon />;
            case 'service-inspection-new':
                return <ServiceNewIcon />;
            case 'service-inspection-failed-contact':
                return <ServiceFailedIcon />;
            case 'service-inspection-accepted':
                return <ServiceProgressIcon />;
            case 'service-inspection-pending':
                return <ServiceProgressIcon />;
            case 'service-inspection-in-progress':
                return <ServiceProgressIcon />;
            case 'service-inspection-fixed':
                return <ServiceProgressIcon />;
            case 'service-inspection-closed':
                return <ServiceProgressIcon />;
            case 'device-deployment-ready-for-deployment':
                return <NewDeviceReadyForDeploymentIcon />;
            case 'device-deployment-production-done':
                return <NewDeviceProductionDoneIcon />;
            case 'device-deployment-new':
                return <NewDeviceNewIcon />;
            default:
                return null;
        }
    };

    return (
        <div className='notification-list-item-content'>
            {Array.from(doc.body.childNodes).map((node, index) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    return <span key={index}>{node.textContent}</span>;
                }
                if (node.nodeName === 'STRONG') {
                    return <strong key={index}>{node.textContent}</strong>;
                }
                if (node.nodeName === 'DIV' && node.className === 'tickets') {
                    const firstNode = node.childNodes[0];
                    const startsWithText = firstNode?.nodeType === Node.TEXT_NODE && firstNode.textContent.trim() !== '';

                    const wrapperClassName = `tickets ${startsWithText ? 'starts-with-text' : 'starts-with-span'}`;

                    return (
                        <div key={index} className={wrapperClassName}>
                            {Array.from(node.childNodes).map((ticketNode, ticketIndex) => {
                                if (ticketNode.nodeName === 'SPAN' && ticketNode.className === 'ticket') {
                                    const iconClass = ticketNode.querySelector('i')?.className;
                                    return (
                                        <span key={ticketIndex} className='ticket'>
                                            {iconClass && renderIcon(iconClass)}
                                            {ticketNode.childNodes[1]?.textContent}
                                        </span>
                                    );
                                }
                                if (ticketNode.nodeType === Node.TEXT_NODE) {
                                    return <span key={ticketIndex}>{ticketNode.textContent}</span>;
                                }
                                if (ticketNode.nodeName === 'I' && ticketNode.className === 'arrow') {
                                    return <i key={ticketIndex} className='arrow' />;
                                }
                                return null;
                            })}
                        </div>
                    );
                }
                return null;
            })}
        </div>
    );
};

const NotificationListItem = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const refreshCount = useRefreshNotificationCount();
    const [update] = useUpdate(
        'notification',
        {
            id: record.id,
            data: { isRead: true },
        },
        {
            onSuccess: refreshCount,
        },
    );

    const handleClick = () => {
        if (!record.isRead) {
            update();
        }
    };

    const bgColor = record.isRead ? Palette.gray.gray50 : `${Palette.yellow.yellow50} !important`;

    return (
        <ListItem>
            <ListItemButton component={Link} to={record.link} onClick={handleClick} sx={{ backgroundColor: bgColor }}>
                <ListItemIcon className='notification-list-item-icon'>
                    {!record.isRead ? <ShowIcon /> : <NotificationCategoryIcon category={record.category} />}
                </ListItemIcon>
                <ListItemText
                    primary={<NotificationContent content={record.content} />}
                    secondary={
                        record.createdAt ? (
                            <Tooltip
                                title={
                                    formatIntervalSince(record.createdAt) ? `${formatIntervalSince(record.createdAt)} ${translate('notification.timeAgo')}` : ''
                                }
                                arrow
                                classes={{ tooltip: 'tooltip-black' }}
                            >
                                <span>{format(new Date(record.createdAt), 'dd.MM.yyyy HH:mm')}</span>
                            </Tooltip>
                        ) : (
                            ''
                        )
                    }
                />
                {!record.isRead ? (
                    <Tooltip title={translate('notification.markAsRead')} arrow classes={{ tooltip: 'tooltip-black' }}>
                        <ReadIconButton />
                        <i></i>
                    </Tooltip>
                ) : (
                    <Tooltip title={translate('notification.markAsUnread')} arrow classes={{ tooltip: 'tooltip-black' }}>
                        <ReadIconButton />
                        <i></i>
                    </Tooltip>
                )}
            </ListItemButton>
        </ListItem>
    );
};

export default NotificationList;

const NotificationCategoryIcon = ({ category }) => {
    switch (category) {
        case 'transactions':
            return <NotificationDefaultIcon />;
        case 'service':
            return <NotificationService2Icon />;
        case 'logistics':
            return <NotificationLogisticsIcon />;
        case 'devices':
            return <NotificationDevicesIcon />;
        case 'products':
            return <NotificationProductsIcon />;
        case 'analytics':
            return <NotificationAnalyticalIcon />;
        case 'users':
            return <NotificationUsersIcon />;
        default:
            return <NotificationDefaultIcon />;
    }
};

NotificationCategoryIcon.propTypes = {
    category: PropTypes.string.isRequired,
};
